// Body Ballancer treatment pathways — the single source for the "Five Pathways"
// grid on /treatments/body-ballancer AND the five /treatments/body-ballancer/<id>
// detail pages.
//
// These five sections used to be rendered inline on the hub page: ~2,000 words of
// deep-dive copy where every visitor wanted one section and scrolled past four.
// Worse, five distinct searches — "post-operative lymphatic drainage", "cellulite
// reduction", "glymphatic system", "DOMS recovery", "liver detox" — shared one
// page's title tag and one page's search surface between them. The hub was doing
// 3.7 views a day with full schema markup and flat month-over-month traffic; the
// on-page funnel was fine (7.6% of sessions reached booking submit), so the
// constraint was never conversion, it was that almost nobody arrived.
//
// Same discipline as app/data/nutritionServices.ts, and a plain module with NO
// "use client" for the same reason: server components import it for metadata and
// JSON-LD, and re-exporting shared data through a client module makes .map()
// throw at build time (see app/data/topics.ts).
//
// `id` is the URL slug, the in-page #anchor on the hub, and the JSON-LD @id
// suffix. One identifier, three uses — do not let them drift apart.
//
// Note the spelling: it is body-ballancer everywhere — double L, the Ballancer
// Pro brand spelling. The athletes blog post shipped with a single-L slug on
// 2026-07-14; it was renamed on 2026-09-08 and the old URL 301s in
// next.config.ts. There is no single-L spelling left on the site, so do not
// reintroduce one.

export type PathwayFaq = { q: string; a: string };

export type PathwaySection = {
  /** Question-shaped where it reads naturally — those get cited by AI Overviews. */
  h2: string;
  body: string[];
};

/** A staged clinical timeline (post-op protocol, liver detox phases). */
export type PathwayPhase = { phase: string; timing: string; body: string };

/** A named sub-topic card. `testimonial` is rendered as an attributed quote. */
export type PathwayCard = {
  heading: string;
  tag?: string;
  body: string;
  testimonial?: string;
};

export type BallancerPathway = {
  /** URL slug, hub #anchor and JSON-LD id. Lowercase, hyphenated. */
  id: string;
  /** Ordinal shown on the hub card. */
  num: string;
  symbol: string;
  /** Hub card heading and breadcrumb leaf. */
  name: string;
  /** Hub card copy. Kept short — the page is where the detail lives now. */
  cardBody: string;

  /** <title>, before the brand suffix the page appends. Aim 50–60 chars. */
  title: string;
  /** Meta description. Aim 140–160 chars, written for a human reading a SERP. */
  metaDescription: string;
  h1: string;
  /**
   * The direct answer, in the first ~60 words. Both Google and the AI retrievers
   * weight the top of the page disproportionately, so this answers "what is this
   * and who is it for" before any setup.
   */
  lede: string;

  sections: PathwaySection[];
  phases?: { heading: string; intro?: string; items: PathwayPhase[]; footnote?: string };
  cards?: { heading: string; items: PathwayCard[] };
  checklist?: { heading: string; items: string[] };

  faqs: PathwayFaq[];
  /** blogs.json slugs. Rendered only if the slug still resolves — see the page. */
  relatedBlogs: string[];
  /** Other pathway ids to cross-link. Keeps every page two clicks from the rest. */
  relatedPathways: string[];
  keywords: string[];
  /** ISO date. Shown on the page and used as schema dateModified. */
  lastUpdated: string;
};

/**
 * The "before" state. Lives here rather than inline on the liver-detox pathway
 * because the hub's outcomes arc opens with it — one list, two places, so a
 * change to the symptoms can't leave the two pages disagreeing.
 */
export const SLUGGISH_LYMPH_SIGNS: string[] = [
  "Persistent fatigue",
  "Chronic bloating",
  "Puffy skin and fluid retention",
  "Brain fog",
  "Recurrent illness",
  "Low motivation",
  "Skin breakouts",
  "Cellulite build-up",
];

export const BALLANCER_PATHWAYS: BallancerPathway[] = [
  {
    id: "post-surgery-recovery",
    num: "01",
    symbol: "⟁",
    name: "Post-Operative Recovery",
    cardBody:
      "Reduce post-surgical swelling, accelerate healing, prevent cellulitis and fibrosis, and restore functional movement after joint replacements, abdominal procedures, and cosmetic surgery.",
    title: "Post-Operative Lymphatic Drainage",
    metaDescription:
      "Lymphatic drainage after surgery — reduce swelling, lower fibrosis risk and restore movement. The clinical protocol, timings and what to expect.",
    h1: "Post-Operative Lymphatic Drainage",
    lede:
      "Lymphatic drainage after surgery reduces post-operative swelling, supports the return of functional movement, and lowers the risk of complications such as cellulitis and fibrosis. Sessions typically begin 5–7 days after your operation, once the surgical site is stable, and always in consultation with your surgical team.",
    sections: [
      {
        h2: "What every patient should know before — and after — their operation",
        body: [
          "Post-operative lymphatic drainage is no longer a nice-to-have. It is fast becoming a clinical standard of care — and with good reason. Surgery, however routine, creates trauma to the surrounding tissue. That trauma triggers an inflammatory cascade. The lymphatic system, responsible for clearing that excess fluid and metabolic debris, is often overwhelmed at exactly the time the patient is least able to move.",
        ],
      },
      {
        h2: "Why does sedentary recovery make lymphatic drainage more urgent?",
        body: [
          "When we are forced to rest following surgery, the lymphatic system — which depends entirely on movement and muscle contraction to propel fluid — slows dramatically. This is not merely uncomfortable. A sluggish lymphatic system during recovery means slower fluid clearance, more persistent swelling, a heavier inflammatory burden, impaired immune response, and an increased risk of post-operative complications including cellulitis.",
          "The result is often that patients feel heavier, more fatigued, more demotivated, and less equipped to do the rehabilitation work that determines their long-term outcome. Pro Balancer therapy directly addresses this by doing what the body cannot do for itself during forced rest.",
        ],
      },
    ],
    phases: {
      heading: "The clinical protocol: when to start and how often",
      intro:
        "Based on published post-operative lymphatic drainage guidelines, the following framework applies — always in consultation with your surgical team:",
      items: [
        {
          phase: "Phase One",
          timing: "Days 5–7",
          body: "Earliest point to begin, once the surgical site is stable. Gentle, broad lymphatic stimulation begins to move post-surgical fluid away from the area.",
        },
        {
          phase: "Phase Two",
          timing: "2–3× per week",
          body: "Weeks 1–4 post-surgery. The most critical window. Regular sessions at this frequency significantly reduce swelling, lower risk of fibrosis, and accelerate the return of functional range of motion.",
        },
        {
          phase: "Phase Three",
          timing: "1× per week",
          body: "Weeks 4–8 and beyond. Maintenance and consolidation. As healing progresses, weekly sessions sustain lymphatic flow, support scar tissue management (from week 6 when skin is fully closed), and continue to support the immune response.",
        },
      ],
      footnote:
        "Always follow your surgeon’s specific post-operative guidance. Scar tissue work begins only once the wound is fully closed and shows no signs of infection, typically 6–8 weeks post-surgery.",
    },
    cards: {
      heading: "Procedures we most often support",
      items: [
        {
          heading: "Knee & Hip Replacement",
          tag: "Joint Replacement",
          body: "Joint replacement surgery disrupts the lymphatic network around the operative site, creating significant post-surgical oedema that directly limits the range of movement needed for effective physiotherapy. Lymphatic drainage has been shown to reduce this swelling from the first session, improving comfort, accelerating the return of mobility, and allowing patients to engage more productively with their rehabilitation programme.",
          testimonial:
            "From the first session, the swelling visibly reduced and moving became easier. By the end of the programme, my recovery was ahead of everything the surgical team had predicted. — Total Knee Replacement Patient",
        },
        {
          heading: "Endometriosis & Abdominal Surgery",
          tag: "Abdominal Recovery",
          body: "Abdominal surgery — whether for endometriosis, gynaecological conditions, or digestive pathology — creates significant disruption to the lymphatic vessels of the pelvis and abdomen. Post-surgical adhesions, bloating, and persistent oedema are common. Targeted lymphatic drainage not only accelerates fluid clearance but helps prevent the formation of fibrotic adhesion tissue that can be the source of long-term discomfort and complication.",
          testimonial:
            "The bloating and heaviness after my laparoscopy was debilitating. After three sessions, I felt like myself again — lighter, less inflamed, and finally able to move without pain. — Endometriosis Patient",
        },
        {
          heading: "Postpartum Recovery",
          tag: "Post-Birth",
          body: "Whether following vaginal birth or caesarean section, the postpartum body carries an extraordinary fluid load alongside the demands of sleep deprivation, hormonal fluctuation, and the physical toll of feeding and caring for a newborn. Lymphatic drainage helps the body clear retained fluid rapidly, supports the healing of abdominal and perineal tissue, and — at a time when motivation and energy are already stretched thin — provides a profound sense of physical relief and restoration that supports emotional wellbeing too.",
          testimonial:
            "Within one session I could see the difference in my abdomen and I felt so much lighter. I only wish I’d known about this sooner. — Postpartum Client",
        },
        {
          heading: "Liposuction & Cosmetic Surgery",
          tag: "Aesthetic Recovery",
          body: "Liposuction and cosmetic procedures carry a particularly high lymphatic demand: the disruption to subcutaneous lymphatic vessels means fluid redistribution is significant and, without active intervention, fibrotic nodules and uneven skin texture are common. Post-operative lymphatic drainage is widely recommended by plastic surgeons as an essential component of the recovery protocol — reducing contour irregularities, minimising bruising, preventing fibrosis, and dramatically shortening recovery timelines.",
          testimonial:
            "My plastic surgeon told me the quality of my result would depend on my post-op care. This treatment made the difference between a good outcome and an exceptional one. — Liposuction Patient",
        },
      ],
    },
    faqs: [
      {
        q: "How soon after surgery can I start lymphatic drainage?",
        a: "The earliest typical starting point is 5–7 days after your operation, once the surgical site is stable. This varies by procedure and by patient, so we ask you to confirm the timing with your surgeon or surgical team before your first session. We are happy to work alongside their guidance.",
      },
      {
        q: "How many sessions will I need after surgery?",
        a: "Most post-operative protocols run 2–3 sessions per week for the first four weeks — the window where drainage makes the most difference — then reduce to weekly for weeks 4–8. The blocks of 5 and 10 sessions exist for exactly this pattern and work out at £40 and £35 per session respectively.",
      },
      {
        q: "Will lymphatic drainage help with post-surgical scarring?",
        a: "Scar tissue work is part of the later phase of the protocol, and only begins once the wound is fully closed and shows no signs of infection — typically 6–8 weeks after surgery. Before that point, sessions focus on fluid clearance and swelling rather than the scar itself.",
      },
      {
        q: "Is it safe to have lymphatic drainage after cosmetic surgery?",
        a: "Post-operative lymphatic drainage is widely recommended by plastic surgeons as part of the recovery protocol following liposuction and similar procedures. As with any post-surgical treatment, we work in conjunction with — and with the approval of — your surgical team.",
      },
      {
        q: "Who should not have post-operative lymphatic drainage?",
        a: "Common contraindications include active infection at or near the surgical site, deep vein thrombosis (DVT), severe heart conditions, and certain skin conditions. We review your individual circumstances before your first session, and we will decline to treat if it is not appropriate.",
      },
    ],
    relatedBlogs: ["anti-inflammatory-digestive-elixir", "unlock-your-bodies-natural-detox-potential-gutbuster"],
    relatedPathways: ["injury-prevention", "cellulite-and-skin"],
    keywords: [
      "post-operative lymphatic drainage",
      "lymphatic drainage after surgery",
      "post surgery swelling treatment",
      "post-op recovery Southport",
      "lymphatic drainage after liposuction",
      "knee replacement swelling",
      "postpartum lymphatic drainage",
      "endometriosis surgery recovery",
      "post-surgical fibrosis prevention",
      "cellulitis risk reduction",
    ],
    lastUpdated: "2026-09-08",
  },

  {
    id: "injury-prevention",
    num: "02",
    symbol: "◎",
    name: "Injury Prevention & Athletic Recovery",
    cardBody:
      "Release myofascial restrictions, rebalance overused and underused muscle groups, reduce DOMS after hard training, and correct the loading patterns that cause injury before it happens.",
    title: "Injury Prevention & Athletic Recovery",
    metaDescription:
      "Reduce DOMS, release myofascial restriction and correct the loading patterns behind most injuries — with Niki Charnock, 25+ years in elite sport.",
    h1: "Injury Prevention & Athletic Recovery",
    lede:
      "Most injuries are not accidents — they are the predictable result of imbalances that accumulate over months and years. Combined lymphatic drainage and myofascial release clears inflammatory by-products after hard training, restores fascial suppleness, and addresses the loading patterns that cause breakdown before it happens.",
    sections: [
      {
        h2: "Why do most injuries build over time rather than happen by accident?",
        body: [
          "Unless you have been in a collision — a car accident, a rugby tackle, a sudden traumatic event — the vast majority of injuries are not random. They are the predictable result of imbalances that accumulate quietly, over months and years, until the point of failure.",
          "Your body is highly adaptive. It will mould itself to the positions and demands you place on it — and when those demands are uneven, imbalanced, or repetitive, the consequences are structural.",
          "Prolonged sitting tightens the hip flexors and quadriceps whilst the glutes — arguably the most important force-generating muscle group in the body — progressively disengage. Tight hip flexors and inactive glutes are one of the most well-evidenced contributors to lower back pain and hip pathology, and yet they are so common that most people accept them as an inevitable part of modern life.",
          "Regular fascial and lymphatic work is not simply recovery. It is maintenance of the human machine. Think of it the same way you think about servicing a car: consistent, proactive attention prevents the breakdown that costs far more — in time, pain, and lost training — than the maintenance ever would.",
        ],
      },
      {
        h2: "How does lymphatic drainage reduce DOMS?",
        body: [
          "Delayed Onset Muscle Soreness (DOMS) is the result of micro-trauma and the subsequent inflammatory response in muscle tissue. Lymphatic drainage directly supports the clearance of these inflammatory by-products from the tissue, reducing the intensity and duration of soreness.",
          "Schedule your session following the hardest training session of your week — not only will you recover faster, but the session can be adapted to specifically target the muscle groups under the greatest load, giving you a personalised recovery protocol that compounds your training benefit.",
        ],
      },
    ],
    checklist: {
      heading: "Common patterns we address",
      items: [
        "Desk worker hip pattern — shortened hip flexors, inhibited glutes, anterior pelvic tilt: the foundation of most chronic lower back pain.",
        "Runner’s fascial loading — ITB tightness, plantar fascia restriction, and calf/Achilles overload from repetitive unilateral loading patterns.",
        "Cyclist’s anterior chain dominance — compressed hip flexors, thoracic kyphosis, neck tension, creating shoulder and knee vulnerability.",
        "Swimmer’s shoulder — overloaded rotator cuff and lat dominance creating internal rotation restriction and impingement risk.",
        "Team sport asymmetry — dominant leg loading patterns, adductor/abductor imbalance, and repetitive rotational forces through the lumbar spine.",
        "Post-surgical compensation — the entire body adapts its movement patterns around pain, creating secondary imbalances that outlast the original injury.",
      ],
    },
    faqs: [
      {
        q: "When is the best time to book a session around my training?",
        a: "Directly after the hardest training session of your week. That is when the inflammatory load in the tissue is highest and when clearing it makes the most difference to how quickly you are ready to train again. The session can also be adapted to target the muscle groups that took the greatest load.",
      },
      {
        q: "Can lymphatic drainage really prevent injury, or just help me recover?",
        a: "Both, but by different mechanisms. Recovery is the clearance of inflammatory by-products after load. Prevention is the fascial side of the work — restoring suppleness to tissue that has become restricted through repetitive loading, and addressing the imbalances that build quietly until something fails.",
      },
      {
        q: "How often should an athlete have sessions?",
        a: "Weekly through a heavy training block is typical, dropping to fortnightly for maintenance. The blocks of 5 and 10 sessions are priced for that cadence at £40 and £35 per session respectively.",
      },
      {
        q: "Is this suitable if I train recreationally rather than competitively?",
        a: "Yes — and the desk worker hip pattern above is the single most common thing we see. You do not need to be an athlete to accumulate the loading imbalances that cause injury; a sedentary job produces them just as reliably as a training programme does.",
      },
      {
        q: "Does the session hurt?",
        a: "No. The compression element is gentle and rhythmic — most clients describe it as deeply relaxing, similar to a full-body massage — and pressure levels are adjustable to your comfort. Myofascial release work can be more direct, and we work within your tolerance.",
      },
    ],
    relatedBlogs: [
      "body-ballancer-pro-for-athletes-and-gym-members-southport",
      "full-day-reset-energy-sleep-blood-sugar-gut-health",
    ],
    relatedPathways: ["post-surgery-recovery", "brain-health"],
    keywords: [
      "DOMS recovery treatment",
      "athlete recovery Southport",
      "myofascial release therapy",
      "injury prevention therapy",
      "sports recovery lymphatic drainage",
      "hip flexor tightness treatment",
      "runner recovery therapy",
      "swimmer’s shoulder treatment",
      "lower back pain glutes",
      "pneumatic compression athletes",
    ],
    lastUpdated: "2026-09-08",
  },

  {
    id: "cellulite-and-skin",
    num: "03",
    symbol: "✦",
    name: "Cellulite & Skin Health",
    cardBody:
      "Improve skin texture, reduce the appearance of cellulite, and enhance lymphatic flow through superficial tissue using targeted drainage and myofascial release techniques.",
    title: "Cellulite Reduction & Skin Health",
    metaDescription:
      "Cellulite is a lymphatic and fascial problem, not simply a fat one. How drainage and myofascial release improve skin texture progressively.",
    h1: "Cellulite Reduction & Skin Health",
    lede:
      "Cellulite is not simply a fat storage issue — it is the result of impaired lymphatic drainage and compromised circulation in the subcutaneous tissue. Combining precision pneumatic drainage with targeted myofascial release addresses both the fluid congestion and the fascial restriction that create the dimpled appearance.",
    sections: [
      {
        h2: "Why is cellulite a lymphatic problem?",
        body: [
          "Cellulite is not simply a fat storage issue — it is the result of impaired lymphatic drainage and compromised circulation in the subcutaneous tissue. When lymph pools in the connective tissue beneath the skin, it creates the characteristic dimpled appearance associated with cellulite. The fascia — the connective tissue network that envelopes every muscle, organ, and structure in the body — becomes thickened and fibrous where drainage is poor.",
          "Our protocol combines the Pro Balancer’s precision pneumatic drainage with targeted myofascial release to directly address both the fluid congestion and the fascial restriction. The result is not a temporary fix — it is a genuine improvement in tissue quality, circulation, and skin texture that develops progressively with consistent treatment.",
          "Smoother, firmer, healthier-looking skin is not a cosmetic illusion. It is the visible evidence of a lymphatic system that is doing its job — and tissue that is being properly nourished, drained, and maintained.",
        ],
      },
    ],
    cards: {
      heading: "How the protocol works",
      items: [
        {
          heading: "Myofascial Release",
          body: "Fascia that has become thickened and restricted through poor drainage, repetitive stress, or inactivity directly contributes to cellulite formation. Myofascial release techniques restore the fascia’s natural suppleness, allowing fluid to move freely through the tissue.",
        },
        {
          heading: "Subcutaneous Drainage",
          body: "Pro Balancer’s calibrated compression sequences work at the level of the superficial lymphatic network — the fine vessels that drain the skin and subcutaneous fat layer — clearing the stagnant fluid that creates the dimpling effect.",
        },
        {
          heading: "Collagen & Tissue Nutrition",
          body: "When lymphatic flow is restored, so is the effective delivery of nutrients to the skin cells and the efficient removal of waste products. This supports the healthy collagen environment that gives skin its firmness, elasticity, and glow.",
        },
        {
          heading: "Progressive Improvement",
          body: "Results are cumulative. A course of sessions produces visible, lasting improvement in skin texture — not the temporary puffiness of water retention manipulation, but a genuine structural improvement in tissue quality.",
        },
      ],
    },
    faqs: [
      {
        q: "How many sessions before I see a difference in skin texture?",
        a: "Results here are cumulative rather than immediate. Many clients notice reduced puffiness and a feeling of lightness after a single session, but visible change in skin texture develops over a course — typically 5 to 10 sessions taken weekly or fortnightly.",
      },
      {
        q: "Is the improvement permanent?",
        a: "The improvement is structural rather than a temporary manipulation of water retention, so it holds better than a one-off treatment. It is not permanent in the sense of never needing maintenance: lymphatic flow responds to how you live, so most clients continue at a lower frequency once they have the result they wanted.",
      },
      {
        q: "Will this help with fluid retention and puffiness as well as cellulite?",
        a: "Yes — fluid retention and puffiness usually respond faster than skin texture does, because clearing congested fluid is the more immediate effect. Reduced bloating and a lighter feeling in the legs and abdomen are among the first things clients report.",
      },
      {
        q: "Is this a weight loss treatment?",
        a: "No. Lymphatic drainage addresses fluid congestion and fascial restriction in the subcutaneous tissue, which changes how tissue looks and feels — it does not reduce body fat. Anyone presenting it as weight loss is describing something it does not do.",
      },
      {
        q: "Who should not have this treatment?",
        a: "Common contraindications include active infections, deep vein thrombosis (DVT), severe heart conditions, pregnancy, and certain skin conditions. A pre-treatment consultation allows us to review your individual circumstances.",
      },
    ],
    relatedBlogs: ["unlock-your-bodies-natural-detox-potential-gutbuster"],
    relatedPathways: ["liver-detox", "post-surgery-recovery"],
    keywords: [
      "cellulite reduction treatment",
      "cellulite lymphatic drainage",
      "skin texture improvement",
      "myofascial release cellulite",
      "fluid retention treatment",
      "pressotherapy cellulite",
      "body contouring Southport",
      "subcutaneous lymphatic drainage",
      "puffiness treatment",
      "skin firmness collagen lymph",
    ],
    lastUpdated: "2026-09-08",
  },

  {
    id: "liver-detox",
    num: "04",
    symbol: "▲",
    name: "Liver & Cellular Detox",
    cardBody:
      "Open the lymphatic drainage pathways so the liver’s metabolic by-products, fat-soluble toxins, and cellular waste can be safely transported out of the body.",
    title: "Liver Detox & Lymphatic Drainage",
    metaDescription:
      "Any liver detox that ignores lymphatic flow is incomplete — fat-soluble toxins leave only via the lymph. The three phases, and where drainage fits.",
    h1: "Liver Detox & The Lymphatic System",
    lede:
      "The liver processes toxins, but it cannot remove them alone. Fat-soluble toxins, metabolic by-products and excess fats are transported out of the body exclusively through the lymphatic system — not the bloodstream. If the lymph is sluggish, processed toxins recirculate and redeposit, which is why a liver detox protocol that ignores lymphatic flow is incomplete.",
    sections: [
      {
        h2: "Why can’t the liver complete detoxification on its own?",
        body: [
          "The liver is the body’s master detoxification organ — processing environmental toxins, metabolic by-products, used hormones, pharmaceutical residues, food additives, and the constant stream of cellular waste that every living body generates simply by being alive. In the modern world, that burden has never been greater.",
          "But here is what most detox protocols miss entirely: the liver cannot complete its work alone. Once the liver has processed toxins through its two-phase enzymatic pathway, those by-products must be transported out of the body. That transport system is the lymphatic network.",
          "Critically, fat-soluble toxins, metabolic by-products, and excess fats are moved exclusively through the lymphatic system — not the bloodstream. If the lymph is sluggish, those processed toxins have nowhere to go. They recirculate. They redeposit in tissue. They contribute to the very toxic load the detox protocol was designed to reduce.",
          "Any serious liver detoxification protocol that does not explicitly address lymphatic flow is incomplete. Lymphatic drainage is not an optional extra to a liver cleanse — it is the drainage pipe without which the drain cannot function.",
        ],
      },
      {
        h2: "The open drain principle",
        body: [
          "Think of your body’s detox system as a series of drains, each feeding into the next. The lymphatic system must be open and flowing before the liver can effectively offload its processed waste. A blocked lymph is the equivalent of a sink with no drain — no matter how powerful the tap, nothing moves. Every liver detox, cellular cleanse, or anti-inflammatory protocol becomes dramatically more effective when the lymphatic drainage pathway is actively supported.",
        ],
      },
    ],
    phases: {
      heading: "Liver detox: the three phases",
      items: [
        {
          phase: "Phase 1",
          timing: "Transformation",
          body: "The liver’s cytochrome P450 enzyme system converts fat-soluble toxins into intermediate metabolites. These intermediates are often more reactive than the original toxin and must be rapidly processed in Phase 2.",
        },
        {
          phase: "Phase 2",
          timing: "Conjugation",
          body: "Intermediate metabolites are bound to water-soluble compounds (glutathione, sulphate, glycine) making them safe for excretion. This phase is highly nutrient-dependent and easily overwhelmed by modern toxic load.",
        },
        {
          phase: "Phase 3",
          timing: "Transport & Elimination",
          body: "The completed water-soluble conjugates must now leave the body — via the gut, kidneys, and critically, the lymphatic system. This is the phase where Pro Balancer therapy has its most direct impact.",
        },
      ],
    },
    checklist: {
      heading: "Signs your lymph may be sluggish",
      items: SLUGGISH_LYMPH_SIGNS,
    },
    faqs: [
      {
        q: "Should I have lymphatic drainage before or during a liver detox?",
        a: "Ideally both. Opening the drainage pathway before you begin means the by-products the liver produces have somewhere to go from day one; continuing through the protocol keeps that pathway clear as the toxic load being processed increases.",
      },
      {
        q: "Why do I feel worse partway through a detox protocol?",
        a: "That pattern often reflects Phase 1 outpacing Phases 2 and 3 — the liver converts fat-soluble toxins into reactive intermediates faster than the body can conjugate and remove them. Supporting the elimination end of the chain, lymphatic transport included, is the part most protocols leave out.",
      },
      {
        q: "Does this replace a nutritional detox programme?",
        a: "No — it supports one. The liver’s conjugation phase is highly nutrient-dependent, so the nutritional side does work that drainage cannot. If you want the two designed together, the free 15-minute discovery call is the place to start.",
      },
      {
        q: "How does the Express Mini Detox pack fit with sessions?",
        a: "The offer pairs the Express Mini Detox pack with two Pro Balancer sessions for £130, which covers both ends of the chain — the nutritional support for the liver’s processing phases and the drainage that clears the output. The pack is handed to you at your first session.",
      },
      {
        q: "Is there any downtime after a session?",
        a: "No downtime is required and most clients return to normal activities immediately. We recommend drinking plenty of water afterwards to support the lymphatic process.",
      },
    ],
    relatedBlogs: [
      "non-alcoholic-fatty-liver-disease-whole-system-approach",
      "unlock-your-bodies-natural-detox-potential-gutbuster",
      "heavy-metal-detox-experience",
    ],
    relatedPathways: ["brain-health", "cellulite-and-skin"],
    keywords: [
      "liver detox lymphatic drainage",
      "lymphatic system detox",
      "phase 1 phase 2 liver detoxification",
      "fat soluble toxin elimination",
      "sluggish lymphatic system signs",
      "cellular detox therapy",
      "drainage funnel functional medicine",
      "detox support Southport",
      "glutathione conjugation",
      "toxic load reduction",
    ],
    lastUpdated: "2026-09-08",
  },

  {
    id: "brain-health",
    num: "05",
    symbol: "◇",
    name: "Brain & Glymphatic Health",
    cardBody:
      "Support the brain’s own overnight waste clearance system by keeping the cervical and meningeal drainage pathways it empties into open and flowing.",
    title: "The Glymphatic System & Brain Detox",
    metaDescription:
      "The brain has its own waste clearance network — the glymphatic system — draining into the cervical lymph. How daytime flow supports it overnight.",
    h1: "The Glymphatic System: How Your Brain Detoxes at Night",
    lede:
      "The glymphatic system is the brain’s own dedicated waste clearance network, discovered in 2012. While you sleep, cerebrospinal fluid flushes metabolic by-products and inflammatory proteins from brain tissue — draining into the cervical lymphatic vessels. Keeping that peripheral pathway open by day supports the cleanse that happens by night.",
    sections: [
      {
        h2: "What is the glymphatic system?",
        body: [
          "For decades, scientists assumed the brain had no lymphatic system of its own. We now know differently. The glymphatic system — named as a fusion of “glial cells” and “lymphatic” — is the brain’s own dedicated waste clearance network, discovered only in 2012 and now the subject of intense research worldwide.",
          "It works like this: whilst you sleep, cerebrospinal fluid (CSF) is pumped through channels in the brain tissue, flushing out the metabolic by-products, inflammatory proteins, and toxic debris that have accumulated during your waking hours. During deep sleep, the brain’s interstitial space actually expands by up to 60% — creating the room needed for this remarkable nightly cleanse to take place.",
          "The glymphatic system is responsible for removing amyloid-beta and tau proteins — the very compounds that accumulate in Alzheimer’s disease — as well as reactive oxygen species and pro-inflammatory cytokines. A well-functioning glymphatic system is, quite literally, one of the most powerful tools we have against neurodegeneration.",
        ],
      },
      {
        h2: "How does daytime lymphatic flow affect overnight brain detox?",
        body: [
          "Pro Balancer therapy stimulates peripheral lymphatic flow, opening the cervical and meningeal lymphatic vessels that serve as the brain’s drainage exit point. With those pathways open, the glymphatic system can perform its nightly cleanse more efficiently — clearing amyloid-beta, tau, and oxidative debris from the brain tissue.",
          "A body whose lymphatic system flows freely by day gives the brain the conditions it needs to detox deeply by night. These two systems are not separate — they are a continuum.",
          "We invest in our bodies. We exercise, we eat well, we manage stress. But if the glymphatic system is not supported — if the overnight brain cleanse is compromised by poor peripheral lymphatic flow, poor sleep, or an overwhelmed drainage pathway — then the oxidative load within the brain quietly accumulates, year after year.",
        ],
      },
    ],
    cards: {
      heading: "What the glymphatic system clears",
      items: [
        {
          heading: "Amyloid & Tau Clearance",
          body: "The glymphatic system is the primary pathway for clearing amyloid-beta and tau proteins — the toxic accumulations associated with Alzheimer’s and Parkinson’s disease. Impaired glymphatic function means these proteins accumulate faster than they are cleared, accelerating neurodegeneration.",
        },
        {
          heading: "Oxidative Stress Reduction",
          body: "Every waking hour, the brain generates reactive oxygen species as a by-product of its extraordinary energy demands. The glymphatic system removes these free radicals during sleep. When this clearance is impaired, oxidative stress builds within the nervous system — contributing to brain fog, mood dysregulation, and accelerated cognitive decline.",
        },
        {
          heading: "Sleep Quality & The Feedback Loop",
          body: "The relationship between glymphatic function and sleep is bidirectional: poor glymphatic drainage impairs sleep quality, and poor sleep impairs glymphatic drainage. Supporting peripheral lymphatic flow helps break this cycle — creating the conditions for deeper, more restorative sleep and more effective nightly brain detoxification.",
        },
        {
          heading: "The Cervical Lymph Connection",
          body: "Cerebrospinal fluid and glymphatic waste drain directly into the cervical (neck) lymphatic vessels before entering the broader lymphatic system. This is why Pro Balancer therapy — which works across the whole lymphatic chain including the cervical nodes — has a direct and meaningful influence on the brain’s ability to cleanse itself each night.",
        },
      ],
    },
    faqs: [
      {
        q: "Is the glymphatic system real, or is this a theory?",
        a: "It is a genuine and active field of neuroscience research — the system was first described in 2012 and has been studied intensively since. What is well established is the anatomy: the brain clears waste during sleep, and that waste drains into the cervical lymphatic vessels. How much a peripheral drainage session changes that clearance rate in humans is still being researched, so we describe this as supporting the conditions for it rather than as a treatment for any neurological condition.",
      },
      {
        q: "Can this treat or prevent Alzheimer’s disease?",
        a: "No, and we would be wary of anyone who claimed otherwise. Amyloid-beta and tau are cleared by the glymphatic system, and that is why the research interest exists — but supporting lymphatic flow is not a treatment for, or a means of preventing, any neurodegenerative disease. This page describes a mechanism, not a cure.",
      },
      {
        q: "Will this help with brain fog?",
        a: "Brain fog is one of the more commonly reported changes after a course of sessions, alongside improved energy and clarity. It is also a symptom with many possible causes — blood sugar, sleep, gut health, thyroid — so if it is your main concern, the free 15-minute discovery call is a better starting point than booking a session blind.",
      },
      {
        q: "Does the treatment work on my neck and head?",
        a: "The compression garment works across the whole lymphatic chain, and the cervical nodes at the base of the neck are where cerebrospinal drainage enters the broader lymphatic system. The session is not a head or facial treatment.",
      },
      {
        q: "How does sleep fit into this?",
        a: "Sleep is the part that does the actual work — the glymphatic cleanse happens during deep sleep, not during your session. Drainage keeps the exit route clear; sleep drives the flush. If your sleep is poor, that is the higher-value thing to address first, and it is something we cover in consultation.",
      },
    ],
    relatedBlogs: ["full-day-reset-energy-sleep-blood-sugar-gut-health"],
    relatedPathways: ["liver-detox", "injury-prevention"],
    keywords: [
      "glymphatic system",
      "brain detox lymphatic",
      "cervical lymphatic drainage",
      "amyloid beta clearance",
      "brain fog lymphatic drainage",
      "sleep and brain detoxification",
      "meningeal lymphatic vessels",
      "oxidative stress brain",
      "neurodegeneration prevention lifestyle",
      "cerebrospinal fluid drainage",
    ],
    lastUpdated: "2026-09-08",
  },
];

/**
 * The outcomes arc on the hub: where you're starting → after one session →
 * after a course.
 *
 * This replaced a two-column "You'll Feel It Quickly" / "The Long Game" block.
 * The quick column was genuinely experiential, but the long column answered a
 * different question entirely — someone asking what they will feel like after
 * ten sessions was told "reduced oxidative stress at the cellular level". Every
 * physiological claim from that column is kept, demoted from the headline to the
 * `why` beneath the felt claim, which is where credibility belongs and where it
 * stops competing with the thing the reader actually asked.
 *
 * Nothing here promises an outcome: `felt` lines describe what clients commonly
 * report, and the medical disclaimer renders directly beneath the section.
 */
export type OutcomeItem = {
  /** What you notice. Written in the second person, deliberately concrete. */
  felt: string;
  /** The mechanism underneath it — the old "Long Game" copy, demoted. */
  why?: string;
};

export type OutcomeStage = {
  key: string;
  eyebrow: string;
  heading: string;
  intro: string;
  items: OutcomeItem[];
  /** Expectation-setting shown under the list. */
  note?: string;
};

export const BALLANCER_OUTCOME_ARC: OutcomeStage[] = [
  {
    key: "before",
    eyebrow: "Where most people start",
    heading: "How it feels when lymph is sluggish",
    intro:
      "Lymph has no pump of its own, so it slows when you do — through injury, surgery, stress or simply sitting still for most of the week. It rarely announces itself. It shows up as a set of things you have probably stopped mentioning to anyone.",
    items: SLUGGISH_LYMPH_SIGNS.map((felt) => ({ felt })),
    note:
      "None of these are dramatic on their own, which is exactly why they get lived with. Most people only notice how much they were carrying once it lifts.",
  },
  {
    key: "first-session",
    eyebrow: "After one session",
    heading: "What you notice the same day",
    intro:
      "A single session works as a standalone treatment, and most people feel the difference before they leave. This is what clients most commonly report.",
    items: [
      {
        felt: "Lighter through the legs and abdomen, and visibly less puffy",
        why: "Fluid that had pooled in the tissue finally has somewhere to go.",
      },
      {
        felt: "Clearer-headed, often with a noticeable lift in energy",
        why: "Metabolic waste clearing out of the tissues rather than sitting in them.",
      },
      {
        felt: "Less swollen, if you arrived with post-surgical or post-injury swelling — often from this first session",
        why: "Drainage doing the work your body cannot do for itself while you are resting.",
      },
      {
        felt: "Looser through the hips, shoulders and back",
        why: "Myofascial tension released alongside the compression work.",
      },
      {
        felt: "Deeply relaxed — most people describe the session itself as closer to a full-body massage than a treatment",
      },
    ],
    note:
      "That evening, expect to need the loo more often than usual. That is the point, not a side effect — it is the fluid leaving. Feeling thirsty, or tired and ready for an early night, is also normal and not a sign anything has gone wrong. Keep drinking, aim for 1.5–2 litres across the rest of the day, and sleep when you want to.",
  },
  {
    key: "course",
    eyebrow: "After a course of 5–10",
    heading: "What changes when it stops coming back",
    intro:
      "One session moves what has built up. A course, taken weekly or fortnightly, is what stops it rebuilding in between — which is where the lasting change actually comes from.",
    items: [
      {
        felt: "The bloating and heaviness stop returning between sessions",
        why: "Drainage pathways staying open, rather than being cleared and re-congesting each time.",
      },
      {
        felt: "Recovery stops being the thing that limits your training",
        why: "Lower systemic inflammation and faster clearance of the by-products behind DOMS.",
      },
      {
        felt: "Skin looks firmer and smoother, and cellulite becomes less pronounced",
        why: "A genuine structural improvement in tissue quality — not the temporary effect of manipulating water retention.",
      },
      {
        felt: "Steadier energy day to day, and fewer of the small illnesses that kept catching you",
        why: "Immune function depends on lymphatic circulation; so does efficient cellular detoxification.",
      },
      {
        felt: "You move more freely, with less of the stiffness you had stopped noticing",
        why: "Maintained fascial health and joint integrity, rather than years of unaddressed restriction accumulating.",
      },
      {
        felt: "You sleep better",
        why: "The brain's overnight clearance drains into the lymphatic system — open pathways by day give it better conditions by night.",
      },
    ],
    note:
      "Further out, this is the case for keeping it up rather than treating it as a one-off: open detox pathways, lower inflammatory load and reduced oxidative stress are what a body that ages well is built on. Individual results vary, and how long the effect holds depends on what you go back to between sessions.",
  },
];

/**
 * Who is realistically within reach of each site, by road.
 *
 * These exist so the hub's `areaServed` schema is earned rather than asserted:
 * Chorley, Leyland and Ormskirk were in the JSON-LD while appearing nowhere in
 * the visible copy, which is exactly the mismatch Google treats as fake
 * structured data. Town names are also distinct query terms — "Preston" does not
 * rank you for "Chorley" — so naming the catchment visibly is what actually
 * captures those searches.
 *
 * Kept here rather than in ballancerLocations.ts: that module is the operational
 * rota and the address env-var mapping, this is page copy.
 */
export const BALLANCER_CATCHMENT: Record<string, string[]> = {
  nicola: ["Preston", "Leyland", "Chorley", "Penwortham", "Bamber Bridge"],
  stockshouse: ["Southport", "Ormskirk", "Burscough", "Formby", "Ainsdale"],
  manchester: ["Manchester", "Salford", "Stockport", "Didsbury", "Altrincham"],
  yote: ["Alderley Edge", "Wilmslow", "Knutsford", "Prestbury", "Macclesfield"],
};

/** The five FAQs shown on the hub page itself. Rendered visibly — see below. */
export const BALLANCER_HUB_FAQS: PathwayFaq[] = [
  {
    q: "Is Body Ballancer therapy safe?",
    a: "Body Ballancer sessions are delivered under clinical oversight. As with any wellness treatment, we recommend a brief consultation beforehand — particularly if you have any health conditions. Our team will discuss your individual circumstances before your first session.",
  },
  {
    q: "Does Body Ballancer hurt?",
    a: "No. Body Ballancer uses gentle, rhythmic compression — most clients describe it as deeply relaxing, similar to a full-body massage. Pressure levels are adjustable to your comfort.",
  },
  {
    q: "How many sessions do I need?",
    a: "Many clients enjoy a single session as a standalone treatment. For more noticeable and lasting results, a course of 5–10 sessions is typically recommended, ideally taken weekly or fortnightly.",
  },
  {
    // The site sells herbal lymphatic support (the Express Mini Detox pack's
    // Herbal Bombs) AND argues on this very page that lymph has no pump. Both
    // claims were live and nothing reconciled them, so a visitor could read the
    // £60 pack as the cheaper alternative to a £45 session — and the £130 bundle
    // sells them together without ever saying why you would want both. Framed by
    // mechanism rather than "nothing beats the real thing": that is a preference
    // claim and invites "says who", where the mechanism is simply the argument
    // this page already makes.
    q: "Can I just take lymphatic drainage drops or herbs instead?",
    a: "They do a real job, but not this one. Herbs like dandelion, milk thistle and cleavers — the ones in our Express Mini Detox pack — work on the liver and kidney end of the chain and are mildly diuretic, which supports the processing side of detoxification. What nothing taken orally can do is move lymph, because lymph has no pump: it is propelled by movement, breath and muscle contraction, and when those are limited by surgery, injury or a desk job, no supplement substitutes for the mechanical work. That is why the two are sold together at £130 rather than as alternatives — the pack supports what the liver processes, the session clears what it produces.",
  },
  {
    q: "Who shouldn’t have Body Ballancer therapy?",
    a: "Body Ballancer is not suitable for everyone. Common contraindications include active infections, deep vein thrombosis (DVT), severe heart conditions, pregnancy, and certain skin conditions. A pre-treatment consultation allows us to review your individual circumstances.",
  },
  {
    q: "Is there any downtime after a session?",
    a: "No downtime is required. Most clients return to normal activities immediately. We recommend drinking plenty of water after your session to support the lymphatic process.",
  },
];

/**
 * The medical disclaimer. Shared by the hub and every pathway page so the five
 * new URLs cannot drift from the wording that was reviewed on the original.
 */
export const BALLANCER_DISCLAIMER =
  "The Pro Balancer Lymphatic System is a medical-grade therapeutic device. All treatments are delivered under the clinical oversight of Niki Charnock, Naturopathic Nutritionist — trained at the College of Naturopathic Medicine (London), a member of the Association of Naturopathic Practitioners (ANP), with 25+ years of clinical experience in elite sports rehabilitation. Post-operative treatments are offered in conjunction with, and with the approval of, your surgical team. Individual results may vary. This page is for informational purposes and does not constitute medical advice. Always consult your healthcare provider before commencing any new therapeutic protocol following surgery or medical treatment.";

export function getBallancerPathway(slug: string) {
  return BALLANCER_PATHWAYS.find((p) => p.id === slug);
}

/** Sitemap entries for the five pathway pages. The hub is in STATIC_PAGES. */
export function getBallancerPathwayPages(): { path: string; lastmod: string }[] {
  return BALLANCER_PATHWAYS.map((p) => ({
    path: `/treatments/body-ballancer/${p.id}`,
    lastmod: p.lastUpdated,
  }));
}
